import { isNotNil } from "client/Misc/utils";
import {
	DragEvent,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useIsMounted } from "./useIsMounted";
import {
	FileSelectionEntry,
	FolderTraversal,
	SelectionEntry,
	extractFileSelectionEntriesFromDataTransfer
} from "client_legacy/FileUploader/utils";

const LONG_DRAG_OVER_TIME_MS = 400;

export type DragOverEventArgs = {
	types: readonly string[];
};

export type DragOverEventResult = {
	isAccepted: true;
	dropEffect?: "none" | "copy" | "link" | "move";
} | {
	isAccepted: false;
};

export type DropEventArgs = {
	getJsonData<TData = unknown>(type: string): TData | null;

	getFiles: {
		(traversal: FolderTraversal.DeepWithoutDirectories): Promise<readonly FileSelectionEntry[]>;
		(traversal?: FolderTraversal): Promise<readonly SelectionEntry[]>;
	};
};

export type DragOverEventCallbacks = {
	onDragOver?(args: DragOverEventArgs): DragOverEventResult;
	onDragEnter?(args: DragOverEventArgs): void;
	onDragLeave?(args: DragOverEventArgs): void;
	onDrop?(args: DropEventArgs): Promise<void> | void;
	onLongDragOver?(args: DragOverEventArgs): void;
};

export type DragOverEventHandlers = {
	onDragOver?(event: DragEvent): void;
	onDragEnter?(event: DragEvent): void;
	onDragLeave?(event: DragEvent): void;
	onDrop?(event: DragEvent): void;
	isDropTarget: boolean;
};

export function useDragAndDropEvents(callbacks: DragOverEventCallbacks): DragOverEventHandlers {
	const {
		onDragOver,
		onDragEnter,
		onDragLeave,
		onDrop,
		onLongDragOver
	} = callbacks;

	const requiresDragOverEventHandlers: boolean = isNotNil(onDragOver);

	const requiresDropEventHandlers: boolean = isNotNil(onDragOver);

	const dragEnterCounterRef: RefObject<number> = useRef<number>(0);
	const [draggedOverTypes, setDraggedOverTypes] = useState<readonly string[] | undefined>(undefined);
	const isDropTarget: boolean = isNotNil(draggedOverTypes);

	const [hasLongDragOverTriggered, setHasLongDragOverTriggered] = useState<boolean>(false);
	const getIsMounted: () => boolean = useIsMounted();

	useEffect(
		() => {
			if (!draggedOverTypes || hasLongDragOverTriggered || !onLongDragOver) {
				return undefined;
			}

			const timeoutHandle: SetTimeoutHandle = setTimeout(
				() => {
					if (getIsMounted()) {
						onLongDragOver({
							types: draggedOverTypes
						});
						setHasLongDragOverTriggered(true);
					}
				},
				LONG_DRAG_OVER_TIME_MS
			);

			return () => clearTimeout(timeoutHandle);
		},
		[
			draggedOverTypes,
			getIsMounted,
			hasLongDragOverTriggered,
			onLongDragOver
		]
	);

	const dragOverHandler = useCallback(
		(event: DragEvent): void => {
			if (!onDragOver) {
				return;
			}

			const result: DragOverEventResult = onDragOver({
				types: event.dataTransfer.types
			});

			if (result.isAccepted) {
				event.preventDefault();
				if (isNotNil(result.dropEffect)) {
					event.dataTransfer.dropEffect = result.dropEffect;
				}
			}
		},
		[onDragOver]
	);

	const dragEnterHandler = useCallback(
		(event: DragEvent): void => {
			if (dragEnterCounterRef.current > 0) {
				dragEnterCounterRef.current++;
				return;
			}

			event.preventDefault();
			dragEnterCounterRef.current++;
			setDraggedOverTypes(event.dataTransfer.types);

			onDragEnter?.({
				types: event.dataTransfer.types
			});
		},
		[onDragEnter]
	);

	const dragLeaveHandler = useCallback(
		(event: DragEvent): void => {
			dragEnterCounterRef.current--;

			if (dragEnterCounterRef.current > 0) {
				return;
			}

			event.preventDefault();
			setDraggedOverTypes(undefined);
			setHasLongDragOverTriggered(false);

			onDragLeave?.({
				types: event.dataTransfer.types
			});
		},
		[onDragLeave]
	);

	const dropHandler = useCallback(
		async (event: DragEvent): Promise<void> => {
			setDraggedOverTypes(undefined);
			setHasLongDragOverTriggered(false);
			dragEnterCounterRef.current = 0;
			if (onDragLeave) {
				onDragLeave({
					types: event.dataTransfer.types
				});

			}
			if (!onDrop) {
				return;
			}

			event.preventDefault();
			await onDrop({
				getJsonData<TData = unknown>(type: string): TData | null {

					const serializedData: string = event.dataTransfer.getData(type);
					if (!serializedData) {
						return null;
					}

					try {
						return JSON.parse(serializedData);
					} catch (e) {
						console.error(
							"Failed to parse drag data as json",
							e,
							serializedData
						);
						return null;
					}
				},
				getFiles: (
					traversal: FolderTraversal = FolderTraversal.Shallow
				) => {
					return extractFileSelectionEntriesFromDataTransfer(
						event.dataTransfer,
						traversal
					) as Promise<any>; // TypeScript is not smart enough to see it is the same union signature as getFiles
				},
			});
		},
		[onDragLeave, onDrop]
	);

	const optionalDragOverHandler = useMemo(
		(): ((event: DragEvent) => void) | undefined => {
			return requiresDragOverEventHandlers || requiresDropEventHandlers
				? dragOverHandler
				: undefined;
		},
		[dragOverHandler, requiresDragOverEventHandlers, requiresDropEventHandlers]
	);

	const optionalDragEnterHandler = useMemo(
		(): ((event: DragEvent) => void) | undefined => {
			return requiresDragOverEventHandlers
				? dragEnterHandler
				: undefined;
		},
		[dragEnterHandler, requiresDragOverEventHandlers]
	);

	const optionalDragLeaveHandler = useMemo(
		(): ((event: DragEvent) => void) | undefined => {
			return requiresDragOverEventHandlers
				? dragLeaveHandler
				: undefined;
		},
		[dragLeaveHandler, requiresDragOverEventHandlers]
	);

	const optionalDropHandler = useMemo(
		(): ((event: DragEvent) => Promise<void>) | undefined => {
			return requiresDropEventHandlers
				? dropHandler
				: undefined;
		},
		[dropHandler, requiresDropEventHandlers]
	);

	return useMemo(
		(): DragOverEventHandlers => ({
			onDragOver: optionalDragOverHandler,
			onDragEnter: optionalDragEnterHandler,
			onDragLeave: optionalDragLeaveHandler,
			onDrop: optionalDropHandler,
			isDropTarget
		}),
		[
			optionalDragOverHandler,
			optionalDragEnterHandler,
			optionalDragLeaveHandler,
			optionalDropHandler,
			isDropTarget
		]
	);
}